/**
 * Flag emoji → flagcdn country code, so daily-game reveals can render a real flag IMAGE instead of
 * the raw emoji. Android ships no country-flag emoji font, so a regional-indicator pair like 🇫🇷
 * renders as the bare letters "FR" — converting to a code lets <CountryFlag> load the CDN image.
 *
 * Handles:
 *   - regional-indicator pairs (🇫🇷 → 'fr')
 *   - UK home-nation tag-sequence flags (🏴󠁧󠁢󠁥󠁮󠁧󠁿 → 'gb-eng', Scotland/Wales likewise)
 * Anything else (incl. plain 🇬🇧) → its alpha-2, or null when no flag is encoded.
 */

const REGIONAL_INDICATOR_BASE = 0x1f1e6; // 🇦
const TAG_LETTER_BASE = 0xe0061; // tag 'a'
const BLACK_FLAG = 0x1f3f4; // 🏴

/** Tag-letter run (e.g. "gbeng") → flagcdn subdivision code. */
const SUBDIVISION_CODES: Record<string, string> = {
  gbeng: 'gb-eng',
  gbsct: 'gb-sct',
  gbwls: 'gb-wls',
};

/** @returns a flagcdn-ready code ('fr', 'gb-eng'), or null when the emoji encodes no usable flag. */
export function flagEmojiToCountryCode(emoji: string | null | undefined): string | null {
  if (!emoji) return null;
  const codePoints = Array.from(emoji, (ch) => ch.codePointAt(0) ?? 0);

  // UK home-nation tag sequence: 🏴 + tag letters + cancel tag.
  if (codePoints[0] === BLACK_FLAG) {
    const letters = codePoints
      .slice(1)
      .filter((cp) => cp >= TAG_LETTER_BASE && cp <= TAG_LETTER_BASE + 25)
      .map((cp) => String.fromCharCode(cp - TAG_LETTER_BASE + 97))
      .join('');
    return SUBDIVISION_CODES[letters] ?? null;
  }

  // Regional-indicator pair: 🇫🇷 → 'fr'.
  const indicators = codePoints.filter(
    (cp) => cp >= REGIONAL_INDICATOR_BASE && cp <= REGIONAL_INDICATOR_BASE + 25,
  );
  if (indicators.length === 2) {
    return indicators
      .map((cp) => String.fromCharCode(cp - REGIONAL_INDICATOR_BASE + 97))
      .join('');
  }

  return null;
}
